import React, { useEffect, useMemo, useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { STATUS_COLOR, STATUS_LABEL } from '../lib/status.js';
import {
  AGING_COLOR,
  agingBucket,
  agingMinutes,
  formatAging,
  parseName,
} from '../lib/format.js';

const COLS = [
  { key: 'status', label: 'Status', sortable: false, width: 100 },
  { key: 'person_name', label: 'Cliente', sortable: true, width: '1fr' },
  { key: 'contract_id', label: 'Contrato', sortable: true, width: 110 },
  { key: 'olt_id', label: 'OLT', sortable: true, width: 130 },
  { key: 'onu_desc', label: 'Descrição', sortable: true, width: '1fr' },
  { key: 'slot', label: 'Slot/Porta', sortable: false, width: 80 },
  { key: 'aging', label: 'Há', sortable: true, width: 90 },
  { key: 'signal_rx_dbm', label: 'RX dBm', sortable: true, width: 70 },
];

const GRID_COLS = COLS.map((c) =>
  typeof c.width === 'number' ? `${c.width}px` : c.width
).join(' ');

const rowHeight = 38;
const headerHeight = 36;
const muteStyle = {
  display: 'inline',
  fontSize: 11,
  color: '#9ca3af',
};

/**
 * ONUTable v2 — virtualizada (react-virtual), com:
 *   - sort por coluna (click no header)
 *   - cliente: nome limpo (parseName) + subtitle (partner/CTO)
 *   - aging dot colorido por bucket
 *   - paginação footer (controlada pelo MapPage)
 *   - sticky header + scroll-into-view quando externamente selecionado
 */
export function ONUTable({
  onus = [],
  selectedSn,
  onSelectOnu,
  sortBy = 'aging',
  sortOrder = 'asc',
  onSortChange,
  loading,
  emptyHint,
  page,
  pageCount,
  matchedCount,
  onPageChange,
  pageSize,
}) {
  const parentRef = useRef(null);
  const virt = useVirtualizer({
    count: onus.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => rowHeight,
    overscan: 12,
  });

  // Scroll-into-view quando seleção vem do mapa.
  // Intencional: depender só de selectedSn (não de onus/virt) — refazer scroll
  // a cada novo fetch criaria jitter na tabela.
  useEffect(() => {
    if (!selectedSn) return;
    const idx = onus.findIndex((o) => o.onu_sn === selectedSn);
    if (idx >= 0) virt.scrollToIndex(idx, { align: 'auto' });
  }, [selectedSn, onus, virt]);

  const items = virt.getVirtualItems();
  const totalHeight = virt.getTotalSize();

  function handleSort(key) {
    if (!onSortChange) return;
    const col = COLS.find((c) => c.key === key);
    if (!col || !col.sortable) return;
    if (sortBy === key) {
      onSortChange(key, sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      onSortChange(key, key === 'aging' ? 'desc' : 'asc');
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'white' }}>
      {/* HEADER (sticky, fora do scroll virtualizado pra alinhar grid) */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: GRID_COLS,
          padding: '0 10px',
          height: headerHeight,
          alignItems: 'center',
          background: '#f9fafb',
          borderBottom: '1px solid #e5e7eb',
          fontSize: 11,
          color: '#6b7280',
          textTransform: 'uppercase',
          fontWeight: 600,
          letterSpacing: 0.5,
          flex: '0 0 auto',
        }}
      >
        {COLS.map((c) => {
          const active = sortBy === c.key;
          return (
            <div
              key={c.key}
              onClick={() => handleSort(c.key)}
              style={{
                cursor: c.sortable ? 'pointer' : 'default',
                userSelect: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                color: active ? '#111827' : '#6b7280',
              }}
            >
              {c.label}
              {c.sortable && (
                <span
                  style={{
                    fontSize: 12,
                    lineHeight: 1,
                    color: active ? '#111827' : '#6b7280',
                    opacity: active ? 1 : 0.55,
                  }}
                  aria-label={
                    active
                      ? sortOrder === 'asc'
                        ? 'ordenado crescente'
                        : 'ordenado decrescente'
                      : 'ordenável'
                  }
                >
                  {active ? (sortOrder === 'asc' ? '▲' : '▼') : '↕'}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* BODY virtualizado */}
      <div ref={parentRef} style={{ overflow: 'auto', flex: 1 }}>
        {loading && onus.length === 0 && (
          <div style={{ padding: 24, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
            Carregando…
          </div>
        )}
        {!loading && onus.length === 0 && (
          <div style={{ padding: 24, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
            {emptyHint || 'Nenhuma ONU no filtro atual.'}
          </div>
        )}
        <div style={{ height: totalHeight, position: 'relative' }}>
          {items.map((vi) => {
            const o = onus[vi.index];
            if (!o) return null;
            const active = o.onu_sn === selectedSn;
            return (
              <Row
                key={o.onu_sn || vi.key}
                onu={o}
                active={active}
                top={vi.start}
                onClick={() => onSelectOnu?.(o.onu_sn)}
              />
            );
          })}
        </div>
      </div>

      {/* Pagination footer */}
      {pageCount > 1 && (
        <Pager
          page={page}
          pageCount={pageCount}
          pageSize={pageSize}
          matchedCount={matchedCount}
          onChange={onPageChange}
        />
      )}
    </div>
  );
}

function Row({ onu, active, top, onClick }) {
  const name = useMemo(() => parseName(onu.person_name), [onu.person_name]);
  const aging = useMemo(
    () => agingMinutes(onu.status_since || onu.observed_at || onu.last_seen_at),
    [onu.status_since, onu.observed_at, onu.last_seen_at]
  );
  const bucket = agingBucket(aging);
  const dot = AGING_COLOR[bucket];
  const statusColor = STATUS_COLOR[onu.status] || '#9ca3af';
  const statusLbl = STATUS_LABEL[onu.status] || 'unknown';

  const slot = onu.slot ?? onu.slot_olt;
  const port = onu.port ?? onu.port_olt;

  return (
    <div
      onClick={onClick}
      title={onu.person_name /* nome cru pra inspeção */}
      style={{
        position: 'absolute',
        top,
        left: 0,
        right: 0,
        height: rowHeight,
        display: 'grid',
        gridTemplateColumns: GRID_COLS,
        alignItems: 'center',
        padding: '0 10px',
        fontSize: 12,
        background: active ? '#eff6ff' : 'white',
        borderBottom: '1px solid #f3f4f6',
        cursor: 'pointer',
        gap: 0,
      }}
    >
      {/* Status pill */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span
          style={{
            display: 'inline-block',
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: statusColor,
            boxShadow: `0 0 0 2px ${statusColor}33`,
          }}
        />
        <span style={{ color: statusColor, fontWeight: 600, fontSize: 11 }}>{statusLbl}</span>
      </div>

      {/* Cliente — nome limpo + subtitle */}
      <div style={{ overflow: 'hidden' }}>
        <div
          style={{
            color: '#111827',
            fontWeight: 500,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontSize: 12,
          }}
        >
          {name.display || (
            <em style={{ color: '#9ca3af', fontWeight: 400 }}>sem cadastro</em>
          )}
        </div>
        {name.subtitle && (
          <div
            style={{
              fontSize: 10,
              color: '#9ca3af',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              marginTop: 1,
            }}
          >
            {name.subtitle}
          </div>
        )}
      </div>

      {/* Contrato */}
      <div
        style={{
          color: onu.contract_code || onu.contract_id ? '#111827' : '#9ca3af',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {onu.contract_code || onu.contract_id || <span style={muteStyle}>—</span>}
      </div>

      {/* OLT */}
      <div
        style={{
          color: '#374151',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {onu.olt_name || onu.olt_code || (onu.olt_id ? `OLT ${onu.olt_id}` : <span style={muteStyle}>—</span>)}
      </div>

      {/* Descrição (raw SNMP .43.1.9 — what the technician configured on the OLT) */}
      <div
        title={onu.onu_desc || ''}
        style={{
          color: onu.onu_desc ? '#374151' : '#9ca3af',
          fontSize: 11,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {onu.onu_desc || <span style={muteStyle}>—</span>}
      </div>

      {/* Slot/Porta */}
      <div style={{ fontVariantNumeric: 'tabular-nums', color: '#374151' }}>
        {slot != null && port != null ? `${slot}/${port}` : <span style={muteStyle}>—</span>}
      </div>

      {/* Aging com dot */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span
          style={{
            display: 'inline-block',
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: dot,
          }}
        />
        <span style={{ color: '#374151', fontVariantNumeric: 'tabular-nums' }}>
          {formatAging(aging)}
        </span>
      </div>

      {/* RX dBm */}
      <div
        style={{
          color: onu.signal_rx_dbm != null ? '#374151' : '#9ca3af',
          fontVariantNumeric: 'tabular-nums',
          textAlign: 'right',
        }}
      >
        {onu.signal_rx_dbm ?? '—'}
      </div>
    </div>
  );
}

function Pager({ page, pageCount, pageSize, matchedCount, onChange }) {
  const start = page * pageSize + 1;
  const end = Math.min((page + 1) * pageSize, matchedCount || 0);
  return (
    <div
      style={{
        flex: '0 0 auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '8px 12px',
        borderTop: '1px solid #e5e7eb',
        background: '#f9fafb',
        fontSize: 12,
        color: '#6b7280',
      }}
    >
      <span>
        {start.toLocaleString('pt-BR')}–{end.toLocaleString('pt-BR')} de{' '}
        <strong style={{ color: '#111827' }}>
          {(matchedCount ?? 0).toLocaleString('pt-BR')}
        </strong>
      </span>
      <div style={{ display: 'flex', gap: 4 }}>
        <PageBtn disabled={page === 0} onClick={() => onChange(0)}>
          ⏮
        </PageBtn>
        <PageBtn disabled={page === 0} onClick={() => onChange(page - 1)}>
          ←
        </PageBtn>
        <span style={{ alignSelf: 'center', padding: '0 8px', fontVariantNumeric: 'tabular-nums' }}>
          {page + 1} / {pageCount}
        </span>
        <PageBtn disabled={page >= pageCount - 1} onClick={() => onChange(page + 1)}>
          →
        </PageBtn>
        <PageBtn disabled={page >= pageCount - 1} onClick={() => onChange(pageCount - 1)}>
          ⏭
        </PageBtn>
      </div>
    </div>
  );
}

function PageBtn({ children, disabled, onClick }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      style={{
        padding: '4px 10px',
        borderRadius: 6,
        border: '1px solid #e5e7eb',
        background: disabled ? '#f3f4f6' : 'white',
        color: disabled ? '#9ca3af' : '#374151',
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontSize: 13,
        lineHeight: 1,
      }}
    >
      {children}
    </button>
  );
}
